// Batched mutation data for performance optimization

function createMutationObserver({
  onAttributeMutation,
  onChildListMutation,
  onMutationFinished
}) {
  if (!window || typeof window.IntersectionObserver !== 'function') {
    return null;
  }
  const mutationObserverCallback = mutations => {
    const targets = [];
    // Use nested Maps for O(1) batching performance
    // Short-lived Maps are safe since they're discarded after each callback
    const batchedMutations = new Map();
    for (const mut of mutations) {
      if (!(mut.target instanceof HTMLElement)) {
        continue;
      }
      if (mut.type === 'attributes') {
        var _mut$oldValue;
        /*
        			"MutationObserver was explicitly designed to work that way, but I can't now recall the reasoning.
        			I think it might have been something along the lines that for consistency every setAttribute call should create a record.
        			Conceptually there is after all a mutation: there is an old value replaced with a new one,
        			and whether or not they are the same doesn't really matter.
        			And Custom elements should work the same way as MutationObserver."
        			https://github.com/whatwg/dom/issues/520#issuecomment-336574796
        		*/
        const oldValue = (_mut$oldValue = mut.oldValue) !== null && _mut$oldValue !== void 0 ? _mut$oldValue : undefined;
        const newValue = mut.attributeName ? mut.target.getAttribute(mut.attributeName) : undefined;
        if (oldValue !== newValue) {
          var _mut$attributeName;
          onAttributeMutation({
            target: mut.target,
            attributeName: (_mut$attributeName = mut.attributeName) !== null && _mut$attributeName !== void 0 ? _mut$attributeName : 'unknown',
            oldValue,
            newValue
          });
        }
        continue;
      } else if (mut.type === 'childList') {
        var _mut$addedNodes, _mut$removedNodes;
        // In chromium browser MutationRecord has timestamp field, which we should use.
        const timestamp = Math.round(mut.timestamp || performance.now());

        // Get or create timestamp bucket
        let timestampBucket = batchedMutations.get(timestamp);
        if (!timestampBucket) {
          timestampBucket = new Map();
          batchedMutations.set(timestamp, timestampBucket);
        }

        // Get or create target batch within timestamp bucket
        let batch = timestampBucket.get(mut.target);
        if (!batch) {
          batch = {
            target: new WeakRef(mut.target),
            addedNodes: [],
            removedNodes: [],
            timestamp
          };
          timestampBucket.set(mut.target, batch);
        }

        // Accumulate added nodes
        ((_mut$addedNodes = mut.addedNodes) !== null && _mut$addedNodes !== void 0 ? _mut$addedNodes : []).forEach(node => {
          if (node instanceof HTMLElement) {
            batch.addedNodes.push(new WeakRef(node));
          }
        });

        // Accumulate removed nodes
        ((_mut$removedNodes = mut.removedNodes) !== null && _mut$removedNodes !== void 0 ? _mut$removedNodes : []).forEach(node => {
          if (node instanceof HTMLElement) {
            batch.removedNodes.push(new WeakRef(node));
          }
        });
      }
      targets.push(mut.target);
    }

    // Process all batched childList mutations
    for (const timestampBucket of batchedMutations.values()) {
      for (const batch of timestampBucket.values()) {
        onChildListMutation({
          target: batch.target,
          addedNodes: batch.addedNodes,
          removedNodes: batch.removedNodes,
          timestamp: batch.timestamp
        });
      }
    }
    onMutationFinished === null || onMutationFinished === void 0 ? void 0 : onMutationFinished({
      targets
    });
  };
  const observer = new MutationObserver(mutationObserverCallback);
  return observer;
}
export default createMutationObserver;